import { AlertTriangleIcon, LightbulbIcon } from "lucide-react";
import { observer } from "mobx-react-lite";

import { ConfirmationModal, ConfirmationModalVariant } from "@/components/ConfirmationModal";
import { usePlayground } from "@/components/Playground/playground-store";

interface ParsingErrorConfirmationProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  saveType: "create" | "update";
  templateName?: string;
}

const ParsingErrorConfirmation = observer(
  ({ isOpen, onConfirm, onCancel, saveType, templateName }: ParsingErrorConfirmationProps) => {
    const playground = usePlayground();
    const { errorCount, messages: messagesWithErrors } = playground.parsingErrorSummary;

    const getMessageTypeLabel = (role: string) =>
      role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();

    const handleConfirm = () => {
      onConfirm();
    };

    const handleClose = () => {
      onCancel();
    };

    const setIsOpen = (open: boolean) => {
      if (!open) {
        onCancel();
      }
    };

    return (
      <ConfirmationModal
        title="Parsing Errors Detected"
        description="Your prompt template contains Jinja2 parsing errors that may prevent it from working correctly."
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        leaveButtonText="Cancel"
        saveButtonText="Save Anyway"
        handleOnConfirm={handleConfirm}
        handleClose={handleClose}
        headerVariant={ConfirmationModalVariant.warning}
        saveButtonVariant="destructive"
        exitButtonVariant="outline"
        useBlocker={false}
      >
        <div className="space-y-6">
          <div className="rounded-lg border border-amber-300/70 bg-amber-50 p-5">
            <div className="mb-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-200">
                  <AlertTriangleIcon className="h-4 w-4 text-amber-700" />
                </div>
                <h4 className="text-base font-semibold text-gray-900">
                  {errorCount} parsing error{errorCount > 1 ? "s" : ""}
                </h4>
              </div>
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-200 text-xs font-semibold text-amber-800">
                {errorCount}
              </span>
            </div>
            <div className="max-h-72 space-y-4 overflow-y-auto">
              {messagesWithErrors.map(message => (
                <div
                  key={message.id}
                  className="rounded-lg border border-gray-300 bg-white p-4 shadow-sm"
                >
                  <div className="mb-3 flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-800">
                      {getMessageTypeLabel(message.role)}
                    </span>
                  </div>

                  <div className="mb-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                    <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-600">
                      Message Content
                    </div>
                    <div className="max-h-24 overflow-y-auto">
                      <p className="truncate whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700">
                        {message.content || "No content"}
                      </p>
                    </div>
                  </div>

                  <div className="rounded-md border border-red-200 bg-red-50 p-3">
                    <div className="mb-2 text-xs font-medium uppercase tracking-wide text-red-600">
                      Parsing Error
                    </div>
                    <code className="block whitespace-pre-wrap break-words font-mono text-sm leading-6 text-red-800">
                      {message.error}
                    </code>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-5">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-200">
                <LightbulbIcon className="h-4 w-4 text-blue-700" />
              </div>
              <h4 className="text-base font-semibold text-gray-900">Quick Fix Suggestions</h4>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3 rounded-md bg-white p-3">
                <div className="mt-0.5 flex h-5 w-5 items-center justify-center">
                  <span className="text-xs font-semibold text-blue-600">1</span>
                </div>
                <span className="leading-relaxed text-gray-700">
                  Switch from f-string to jinja2 format (e.g.,{" "}
                  <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                    {"{{variable}}"}
                  </code>{" "}
                  instead of{" "}
                  <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                    {"{variable}"}
                  </code>
                  )
                </span>
              </div>
              <div className="flex items-start gap-3 rounded-md bg-white p-3">
                <div className="mt-0.5 flex h-5 w-5 items-center justify-center">
                  <span className="text-xs font-semibold text-blue-600">2</span>
                </div>
                <span className="leading-relaxed text-gray-700">
                  Review template syntax for proper Jinja2 expressions and filters
                </span>
              </div>
              <div className="flex items-start gap-3 rounded-md bg-white p-3">
                <div className="mt-0.5 flex h-5 w-5 items-center justify-center">
                  <span className="text-xs font-semibold text-blue-600">3</span>
                </div>
                <span className="leading-relaxed text-gray-700">
                  Test your template with sample variables to verify functionality
                </span>
              </div>
            </div>
          </div>

          {templateName && (
            <div className="border-t border-gray-200 pt-4 text-sm text-gray-600">
              {saveType === "create" ? "Creating" : "Updating"} template:{" "}
              <span className="font-semibold text-gray-900">{templateName}</span>
            </div>
          )}
        </div>
      </ConfirmationModal>
    );
  },
);

export default ParsingErrorConfirmation;
